/**
 * Crew slice — the roster plus everything the Friends and Profile screens
 * write. Prototype: `savePf` (1928), `savePw` (1934), `onPickAv` (1911),
 * `doAddUser` (1885), the admin row closures (2450–2461), `markReviewed`
 * (1848), `seenUsers` (1853).
 */

import type { StateCreator } from 'zustand';

import type { GameId, SteamLink, User } from '../../lib/types.ts';
import { RepoError } from '../../repo/Repository.ts';
import { mutate, storeApi } from '../mutate.ts';
import type { QdStore } from '../types.ts';
import { PASSWORD_MIN, PASSWORD_MIN_COPY_PROFILE } from './session.ts';

export interface CrewSlice {
  users: User[];
  steamLink: SteamLink | null;

  /**
   * `+ ADD USER` is open. THE ONLY GUARD THERE IS, and it has to exist: the
   * server does not dedupe. `admin-users op=create` derives the login key with
   * `uniqueUserKey`, which BUMPS on collision (`chris` → `chris2`) rather than
   * refusing, and nothing deduplicates the recovery address — so two presses
   * mint two real accounts, two auth users and two roster rows.
   *
   * Worse, the second response overwrites `invite`, and the temp password is
   * returned exactly once and stored nowhere. The owner is left with a member
   * they must delete and a member whose password nobody has.
   */
  addBusy: boolean;
  /**
   * Which member's password is being reset, `''` when none. KEYED, not a
   * boolean: the chips are per-row, and resetting Chris must not freeze
   * Chelsea's. Every press mints a fresh password server-side, so two presses
   * leave the account on the second one while the panel shows whichever
   * response landed last.
   */
  pwBusy: string;

  me: () => User | null;
  user: (k: string) => User | null;

  savePf: () => Promise<void>;
  savePw: () => Promise<void>;
  /** Change the LOGIN address, gated on the current password. */
  saveEmail: () => Promise<void>;
  onPickAv: (dataUrl: string) => Promise<void>;
  /** CHOOSE YOUR COLOR tile pick — sets the hue, clears any custom color. */
  setMyHue: (hue: number) => Promise<void>;
  /** `USE THIS COLOR ▸` — commits the custom field pick and closes the picker. */
  applyCustomColor: (hex: string) => Promise<void>;
  /** `USE THE COLOR ✕` — drops the uploaded image; self-only, one press. */
  dropAv: () => Promise<void>;
  doAddUser: () => Promise<void>;
  doResetPw: (userKey: string) => Promise<void>;
  doRm: (userKey: string) => Promise<void>;
  markReviewed: () => Promise<void>;
  seenUsers: (ids: GameId[]) => Promise<void>;
}

/**
 * `profile:<userKey>` — the §2.3 key for EVERY write that lands on a `profiles`
 * row, which is three of them: `savePf`, `onPickAv` and `markReviewed`.
 *
 * A builder rather than three matching literals, because the table has one
 * realtime subscription and therefore one patch (`member.upsert`), so
 * `patchEchoKey` has exactly one string it can derive. The two that used to
 * spell their own (`avatar:<me>`, `markReviewed:<me>`) were never recognised as
 * their own echo — the same dead ✕ SOMEONE ELSE CHANGED THAT as the
 * `list.ts#removeFromList` bug, and just as silent.
 *
 * Sharing the key also serializes the three against each other, which is what
 * you want for three writers of ONE row: `mutate()` snapshots `users` before
 * applying, so a concurrent avatar upload and profile save could otherwise roll
 * each other back. `seenUsers` is deliberately NOT here — it writes
 * `whats_new_seen`, which emits no patch at all.
 */
export const profileKey = (userKey: string): string => 'profile:' + userKey;

export const createCrewSlice: StateCreator<QdStore, [], [], CrewSlice> = (set, get) => ({
  users: [],
  steamLink: null,
  addBusy: false,
  pwBusy: '',

  me: () => get().users.find((u) => u.k === get().session) ?? null,
  user: (k) => get().users.find((u) => u.k === k) ?? null,

  savePf: async () => {
    const s = get();
    const name = s.pfName.trim();
    if (!name) {
      s.toast('NAME REQUIRED', true);
      return;
    }
    // SAVE NO LONGER OWNS THE EMAIL (docs/ACCOUNT-IDENTITY.md step 2). The
    // address is the LOGIN now, so changing it goes through `saveEmail`, which
    // asks for the current password first. Passing the existing value keeps the
    // legacy `recovery_email` column frozen rather than writing to a field that
    // is on its way out — expand and contract, from the writer's side.
    const email = s.me()?.email ?? '';
    await mutate(storeApi(get, set), {
      key: profileKey(s.session),
      optimistic: (st) => ({
        users: st.users.map((u) => (u.k === st.session ? { ...u, name } : u)),
      }),
      commit: () => s.repo.savePf({ displayName: name, recoveryEmail: email }),
      toast: 'PROFILE SAVED',
    });
  },

  /**
   * Change the account's login address (docs/ACCOUNT-IDENTITY.md step 2).
   *
   * GATED ON THE CURRENT PASSWORD, and that is not ceremony. Confirmations are
   * off, so the change lands immediately with no round trip to the old address
   * — without this door, anyone who got hold of a live session could point the
   * account at their own email and lock the owner out of it. Same gate the
   * password change already uses.
   *
   * A must_reset account is exempt from the gate for the same reason `savePw`
   * exempts it: they are mid-first-login and the password they hold is the temp
   * one they were sent, which `reauth` would reject.
   */
  saveEmail: async () => {
    const s = get();
    const m = s.me();
    if (!m) return;
    if (s.authBusy) return;

    const next = s.emNew.trim().toLowerCase();
    if (!next) {
      s.toast('EMAIL REQUIRED', true);
      return;
    }
    if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(next)) {
      s.toast("✕ THAT DOESN'T LOOK LIKE AN EMAIL", true);
      return;
    }
    if (next === s.pfAuthEmail.toLowerCase()) {
      s.toast('THAT IS ALREADY YOUR EMAIL', true);
      return;
    }

    // Covers BOTH round trips — the reauth below is a full sign-in, and it is
    // the first thing that happens, so a guard set after it would leave the
    // slowest half unguarded.
    set({ authBusy: 'email' });
    try {
      if (!m.mustReset) {
        const okCur = await s.repo.reauth(s.emCur);
        if (!okCur) {
          s.toast('CURRENT PASSWORD IS WRONG', true);
          return;
        }
      }

      try {
        await s.repo.changeEmail(next);
      } catch (e) {
        const code = e instanceof RepoError ? e.code : '';
        // EMAIL_PENDING is not a failure and not a success: GoTrue accepted the
        // request and is waiting on a confirmation link. Saying so is the whole
        // point — this used to report the change as done, and it was not.
        //
        // For an account whose CURRENT address is the synthesized
        // `<user_key>@crew.quarterdown.com`, "secure email change" asks that
        // address to confirm too, and no mail can ever arrive there. So the
        // copy points at the only thing that actually works for them.
        s.toast(
          code === 'EMAIL_TAKEN'
            ? 'THAT EMAIL IS ALREADY IN USE'
            : code === 'EMAIL_PENDING'
              ? 'CHECK YOUR EMAIL TO CONFIRM — NOT CHANGED YET'
              : "✕ COULDN'T CHANGE YOUR EMAIL — TRY AGAIN",
          true,
        );
        return;
      }

      // `pfEmail` moves too: it is what the Profile screen displays, and leaving
      // it on the old address would show a stale answer until the next nav.
      set({ pfAuthEmail: next, pfEmail: next, emOpen: false, emNew: '', emCur: '' });
      // Says what actually changed: the next sign-in uses the new address.
      s.toast('EMAIL CHANGED — SIGN IN WITH IT NEXT TIME');
    } finally {
      set({ authBusy: '' });
    }
  },

  savePw: async () => {
    const s = get();
    const m = s.me();
    if (!m) return;
    if (s.authBusy) return;
    // The two local checks run FIRST now. They used to sit behind the reauth,
    // which is a full sign-in round trip — so mistyping the confirmation cost a
    // network call before the app told you the two boxes did not match.
    if (s.pfN1.length < PASSWORD_MIN) {
      s.toast(PASSWORD_MIN_COPY_PROFILE, true);
      return;
    }
    if (s.pfN1 !== s.pfN2) {
      s.toast('PASSWORDS DO NOT MATCH', true);
      return;
    }
    set({ authBusy: 'password' });
    try {
      if (!m.mustReset) {
        const okCur = await s.repo.reauth(s.pfCur);
        if (!okCur) {
          s.toast('CURRENT PASSWORD IS WRONG', true);
          return;
        }
      }
      // Was uncaught, like `doReset`'s: a refusal rejected a promise every
      // caller discards, so the sheet sat there having done nothing.
      await s.repo.setPassword(s.pfN1);
      set((st) => ({
        users: st.users.map((u) => (u.k === st.session ? { ...u, mustReset: false } : u)),
        pfCur: '',
        pfN1: '',
        pfN2: '',
        // A successful update closes the phone sheet (DESIGN-CHANGELOG
        // 2026-08-18); inert on desktop, whose SECURITY panel is inline.
        pwOpen: false,
      }));
      s.toast('PASSWORD UPDATED');
    } catch {
      s.toast("✕ COULDN'T UPDATE YOUR PASSWORD — TRY AGAIN", true);
    } finally {
      set({ authBusy: '' });
    }
  },

  onPickAv: async (dataUrl) => {
    const s = get();
    await mutate(storeApi(get, set), {
      key: profileKey(s.session),
      optimistic: (st) => ({
        users: st.users.map((u) => (u.k === st.session ? { ...u, av: dataUrl } : u)),
      }),
      commit: () => s.repo.onPickAv(dataUrl),
      toast: 'AVATAR UPDATED',
    });
  },

  // The three CHOOSE YOUR COLOR writers share `profileKey` with savePf /
  // onPickAv — one profiles row, one realtime patch, one echo key. Toasts are
  // the mirrors' copy verbatim (`design/GT Mobile Crew.dc.html` shell actions).
  setMyHue: async (hue) => {
    const s = get();
    await mutate(storeApi(get, set), {
      key: profileKey(s.session),
      optimistic: (st) => ({
        users: st.users.map((u) => (u.k === st.session ? { ...u, hue, col: '' } : u)),
      }),
      commit: () => s.repo.setColor(hue, null),
      toast: 'CHARACTER UPDATED',
    });
  },

  applyCustomColor: async (hex) => {
    const s = get();
    // Close first: the sheet floats over Profile, and the optimistic recolor
    // should be visible on the page it lands on, not behind the overlay.
    s.closeCc();
    await mutate(storeApi(get, set), {
      key: profileKey(s.session),
      optimistic: (st) => ({
        users: st.users.map((u) => (u.k === st.session ? { ...u, col: hex } : u)),
      }),
      commit: () => s.repo.setColor(null, hex),
      toast: 'CUSTOM COLOR APPLIED',
    });
  },

  dropAv: async () => {
    const s = get();
    await mutate(storeApi(get, set), {
      key: profileKey(s.session),
      optimistic: (st) => ({
        users: st.users.map((u) => (u.k === st.session ? { ...u, av: '' } : u)),
      }),
      commit: () => s.repo.dropAvatar(),
      toast: 'IMAGE DROPPED — YOUR COLOR TILE IS BACK',
    });
  },

  doAddUser: async () => {
    const s = get();
    if (s.addBusy) return;
    const name = s.addName.trim();
    if (!name) {
      s.toast('NAME REQUIRED', true);
      return;
    }
    // Emails became REQUIRED on 2026-08-18 (Marcus, X.md §5.1): account
    // recovery and FORGOT PASSWORD? both need one, so a member added without
    // an address is a member who can be locked out permanently.
    //
    // This check has to SHIP BEFORE the reworked `admin-users`, which refuses
    // the call with EMAIL_REQUIRED. A new client against the old function is
    // fine — the old one simply stores the address it always accepted — so
    // the bundle deploys first and the function follows.
    const email = s.addEmail.trim();
    if (!email) {
      s.toast('EMAIL REQUIRED', true);
      return;
    }
    // Deliberately the same permissive shape the function checks
    // (`_shared/crew.ts#isEmail`): catch "marcus" here so the round trip is
    // not the first the admin hears of it, and leave the rest to the server.
    if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(email)) {
      s.toast("✕ THAT DOESN'T LOOK LIKE AN EMAIL", true);
      return;
    }
    // Set BEFORE the first await, so the guard above is already true on the
    // same tick a second press would arrive.
    set({ addBusy: true });
    try {
      const invite = await s.repo.doAddUser({ name, email });
      const snap = await s.repo.fetchCrewSnapshot(s.crewId);
      set({ users: snap.members, addName: '', addEmail: '', invite });
      s.toast('USER ADDED — INVITE READY');
    } catch (e) {
      // EMAIL_TAKEN is a refusal the owner can act on — the address is already
      // on an account — so it must not arrive as the generic try-again.
      const code = e instanceof RepoError ? e.code : '';
      s.toast(
        code === 'EMAIL_TAKEN'
          ? '✕ THAT EMAIL IS ALREADY ON AN ACCOUNT'
          : "✕ COULDN'T ADD THAT USER — TRY AGAIN",
        true,
      );
    } finally {
      set({ addBusy: false });
    }
  },

  doResetPw: async (userKey) => {
    const s = get();
    // Per row: another member's chip stays live.
    if (s.pwBusy === userKey) return;
    set({ pwBusy: userKey });
    try {
      const invite = await s.repo.doResetPw(userKey);
      const snap = await s.repo.fetchCrewSnapshot(s.crewId);
      set({ users: snap.members, invite });
      const u = snap.members.find((x) => x.k === userKey);
      s.toast('TEMP PASSWORD SET FOR ' + (u ? u.name.toUpperCase() : userKey.toUpperCase()));
    } catch {
      s.toast("✕ COULDN'T RESET THAT PASSWORD — TRY AGAIN", true);
    } finally {
      set({ pwBusy: '' });
    }
  },

  doRm: async (userKey) => {
    const s = get();
    const u = s.user(userKey);
    await mutate(storeApi(get, set), {
      key: 'rm:' + userKey,
      optimistic: (st) => ({
        users: st.users.filter((x) => x.k !== userKey),
        list: st.list.map((e) => (e.by === userKey ? { ...e, by: '' } : e)),
        rmArm: '',
      }),
      commit: () => s.repo.doRm(userKey),
      toast: (u ? u.name.toUpperCase() : userKey.toUpperCase()) + ' REMOVED',
    });
  },

  markReviewed: async () => {
    const s = get();
    const now = Date.now();
    await mutate(storeApi(get, set), {
      key: profileKey(s.session),
      optimistic: (st) => ({
        users: st.users.map((u) => (u.k === st.session ? { ...u, lastRev: now } : u)),
      }),
      commit: () => s.repo.markReviewed(),
    });
  },

  seenUsers: async (ids) => {
    const s = get();
    await mutate(storeApi(get, set), {
      key: 'seen:' + s.session,
      optimistic: (st) => ({
        users: st.users.map((u) => {
          if (u.k !== st.session) return u;
          const set2 = new Set<GameId>(u.seen);
          for (const id of ids) if (id != null) set2.add(id);
          return { ...u, seen: [...set2] };
        }),
      }),
      commit: () => s.repo.seenUsers(ids),
    });
  },
});
